/**
 * EVUE Buscador de Blog Ingredientes (v2 — multi-idioma)
 * =======================================================
 * Vanilla JS sin dependencias. Compatible con Dawn y cualquier theme moderno.
 * 
 * Lee las cadenas de UI desde window.evueSearchStrings (inyectado por el snippet).
 * Esto permite que el buscador se traduzca automáticamente al idioma activo.
 * 
 * Funcionalidades:
 * - Autocomplete en vivo vía Shopify Predictive Search API
 * - Debouncing 250ms para evitar saturar la API
 * - Navegación con teclado (↑ ↓ Enter Escape)
 * - Click fuera para cerrar
 * - Submit del form para ir a página de resultados
 * - Multi-idioma vía window.evueSearchStrings
 * - Highlight de términos coincidentes
 * - Excerpt truncado con ellipsis "…" cuando supera EXCERPT_MAX_LENGTH
 */

(function() {
  'use strict';

  // Evitar inicializar varias veces si el snippet se renderiza en home + artículo
  if (window.__evueSearchInitialized) return;
  window.__evueSearchInitialized = true;

  const DEBOUNCE_MS = 250;
  const MIN_QUERY_LENGTH = 2;
  const RESULT_LIMIT = 6;
  const EXCERPT_MAX_LENGTH = 120;

  // Fallbacks si el objeto de strings no se inyectó
  const STRINGS = window.evueSearchStrings || {
    sinResultados: 'No hay ingredientes que coincidan con',
    errorGenerico: 'Algo ha fallado. Inténtalo de nuevo.',
    verTodos: 'Ver todos los resultados →'
  };

  /**
   * Inicializa todas las instancias del buscador de la página
   */
  function init() {
    const containers = document.querySelectorAll('[data-evue-search]');
    containers.forEach(setupSearch);
  }

  function setupSearch(container) {
    const form = container.querySelector('[data-evue-search-form]');
    const input = container.querySelector('[data-evue-search-input]');
    const clearBtn = container.querySelector('[data-evue-search-clear]');
    const resultsBox = container.querySelector('[data-evue-search-results]');

    if (!form || !input || !resultsBox) return;

    let debounceTimer = null;
    let currentQuery = '';
    let activeIndex = -1;
    let resultItems = [];

    // ---- Eventos del input ----
    input.addEventListener('input', handleInput);
    input.addEventListener('keydown', handleKeydown);
    input.addEventListener('focus', () => {
      if (currentQuery.length >= MIN_QUERY_LENGTH) {
        showResults();
      }
    });

    // ---- Botón de limpiar ----
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        input.value = '';
        currentQuery = '';
        hideResults();
        clearBtn.hidden = true;
        input.focus();
      });
    }

    // ---- Click fuera ----
    document.addEventListener('click', (e) => {
      if (!container.contains(e.target)) {
        hideResults();
      }
    });

    // ---- Submit del form: navega a /search?q=...&type=article ----
    form.addEventListener('submit', (e) => {
      const value = input.value.trim();
      if (value.length === 0) {
        e.preventDefault();
        return;
      }
      // Dejamos que el form haga su navegación nativa
      // (acción: /search, hidden type=article ya está en el form)
    });

    // ---- Handler de input con debouncing ----
    function handleInput(e) {
      const query = e.target.value.trim();
      currentQuery = query;
      activeIndex = -1;

      if (clearBtn) clearBtn.hidden = query.length === 0;

      clearTimeout(debounceTimer);

      if (query.length < MIN_QUERY_LENGTH) {
        hideResults();
        return;
      }

      debounceTimer = setTimeout(() => fetchResults(query), DEBOUNCE_MS);
    }

    // ---- Navegación con teclado ----
    function handleKeydown(e) {
      const isVisible = !resultsBox.hidden;

      if (e.key === 'Escape') {
        hideResults();
        input.blur();
        return;
      }

      if (!isVisible || resultItems.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = (activeIndex + 1) % resultItems.length;
        updateActiveItem();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = activeIndex <= 0 ? resultItems.length - 1 : activeIndex - 1;
        updateActiveItem();
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        const href = resultItems[activeIndex].getAttribute('href');
        if (href) window.location.href = href;
      }
    }

    function updateActiveItem() {
      resultItems.forEach((item, i) => {
        if (i === activeIndex) {
          item.setAttribute('aria-selected', 'true');
          item.scrollIntoView({ block: 'nearest' });
        } else {
          item.removeAttribute('aria-selected');
        }
      });
    }

    // ---- Llamada a Predictive Search API ----
    function fetchResults(query) {
      // Usar Shopify.routes.root para soportar /es/ y otros locales
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = root.replace(/\/$/, '') + 
        '/search/suggest.json?q=' + encodeURIComponent(query) + 
        '&resources[type]=article' +
        '&resources[limit]=' + RESULT_LIMIT +
        '&resources[options][unavailable_products]=hide';

      fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(r => r.json())
        .then(data => {
          const articles = (data.resources && data.resources.results && data.resources.results.articles) || [];
          if (currentQuery !== query) return; // ignorar respuestas obsoletas
          renderResults(articles, query);
        })
        .catch(err => {
          console.error('[EVUE Search] Error:', err);
          renderError();
        });
    }

    // ---- Renderizado ----
    function renderResults(articles, query) {
      if (articles.length === 0) {
        resultsBox.innerHTML = `
          <div class="evue-search__state">
            ${STRINGS.sinResultados} <strong>"${escapeHtml(query)}"</strong>
          </div>
        `;
        resultItems = [];
        showResults();
        return;
      }

      const itemsHtml = articles.map(article => {
        const title = highlightMatch(escapeHtml(article.title || ''), query);
        const rawExcerpt = stripHtml(article.summary_html || article.excerpt || '');
        const excerpt = highlightMatch(
          escapeHtml(truncateText(rawExcerpt, EXCERPT_MAX_LENGTH)),
          query
        );
        const imageUrl = getImageUrl(article);
        const initial = (article.title || '?').charAt(0).toUpperCase();

        return `
          <a href="${escapeHtml(article.url)}" class="evue-search__item" role="option">
            ${imageUrl 
              ? `<img src="${escapeHtml(imageUrl)}" alt="" class="evue-search__item-image" loading="lazy">`
              : `<div class="evue-search__item-image evue-search__item-image--placeholder">${initial}</div>`
            }
            <div class="evue-search__item-content">
              <p class="evue-search__item-title">${title}</p>
              ${excerpt ? `<p class="evue-search__item-excerpt">${excerpt}</p>` : ''}
            </div>
          </a>
        `;
      }).join('');

      // Pie con "Ver todos los resultados"
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const searchUrl = root.replace(/\/$/, '') + '/search?q=' + encodeURIComponent(query) + '&type=article';

      resultsBox.innerHTML = itemsHtml + `
        <div class="evue-search__footer">
          <a href="${searchUrl}" class="evue-search__see-all">
            ${escapeHtml(STRINGS.verTodos)}
          </a>
        </div>
      `;

      resultItems = Array.from(resultsBox.querySelectorAll('.evue-search__item'));
      showResults();
    }

    function renderError() {
      resultsBox.innerHTML = `
        <div class="evue-search__state">
          ${escapeHtml(STRINGS.errorGenerico)}
        </div>
      `;
      resultItems = [];
      showResults();
    }

    function showResults() {
      resultsBox.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function hideResults() {
      resultsBox.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    }
  }

  // ---- Utilidades ----
  function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function stripHtml(html) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
  }

  /**
   * Trunca texto a maxLength caracteres añadiendo "…" si se cortó.
   * Hace trimEnd antes del ellipsis para evitar "palabra …".
   */
  function truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text || '';
    return text.substring(0, maxLength).trimEnd() + '…';
  }

  function highlightMatch(text, query) {
    if (!query || !text) return text;
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    return text.replace(regex, '<strong>$1</strong>');
  }

  function getImageUrl(article) {
    if (!article.image) return null;
    if (typeof article.image === 'string') return article.image;
    if (article.image.src) return article.image.src;
    if (article.featured_image) return article.featured_image;
    return null;
  }

  // ---- Arranque ----
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();